import { useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '@/lib/auth/auth-context'

interface WelcomeGuardProps {
  children: React.ReactNode
}

/**
 * WelcomeGuard ensures unauthenticated users see the welcome screen
 * on their first visit, regardless of what URL they navigate to.
 *
 * Authenticated users skip the welcome check entirely.
 */
export function WelcomeGuard({ children }: WelcomeGuardProps) {
  const { user, loading } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()

  useEffect(() => {
    // Wait for auth to resolve before making decisions
    if (loading) return

    // Skip welcome check for authenticated users
    if (user) return

    // Skip if already on welcome page (prevent redirect loop)
    if (location.pathname === '/welcome') return

    // For unauthenticated users: check if they've seen welcome
    const hasSeenWelcome = localStorage.getItem('moneybees_welcome_shown')

    if (hasSeenWelcome !== 'true') {
      console.log('🎉 [WELCOME] First-time visitor detected, redirecting to welcome screen')
      navigate('/welcome', { replace: true })
    }
  }, [user, loading, location.pathname, navigate])

  // Show nothing while auth is loading
  if (loading) {
    return null
  }

  return <>{children}</>
}
